/**
 * Bänder Feature — RTK Query endpoints for assignments (queries).
 *
 * Fetches Band assignments for a class at a given date.
 * Separate from admin queries to keep concerns split.
 *
 * @module baende/api/assignmentQueries
 */

import { rootApi } from "../../../lib/api/rootApi";
import { supabase } from "@flexwise/shared/supabase";

export interface BandAssignment {
  assignment_id: string;
  band_id: string;
  band_name: string;
  band_icon: string;
  band_color: string;
  day_of_week: number;
  period_number: number;
  valid_from: string;
  valid_until: string | null;
}

export const baendeAssignmentQueryApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getBandClassAssignments: builder.query<
      BandAssignment[],
      { classId: string; date?: string }
    >({
      async queryFn({ classId, date }) {
        try {
          const params: Record<string, unknown> = {
            p_class_id: classId,
          };
          if (date) params.p_date = date;

          const { data, error } = await supabase.rpc(
            "fn_baende_get_class_assignments",
            params,
          );

          if (error) {
            return {
              error: {
                status: "CUSTOM_ERROR" as const,
                data: error.message,
                error: error.message,
              },
            };
          }

          return { data: (data as BandAssignment[]) ?? [] };
        } catch (err: unknown) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              data: String(err),
              error: String(err),
            },
          };
        }
      },
      providesTags: (_result, _err, arg) => [
        { type: "BaendeAssignments" as never, id: arg.classId },
      ],
    }),
  }),
});

export const { useGetBandClassAssignmentsQuery } = baendeAssignmentQueryApi;
